import React, {useEffect, useRef, useState} from "react";
import {useOutsiderDetector} from "../../../Hooks/useOutsiderDetector";
import useFilterSelectOptions from "../../../Hooks/useFilterSelectOptions";
import {SelectItemType} from "../../../Types/FormTypes";
import ComboBoxFilter from "../ComboBox/Components/ComboBoxFilter";
import ComboBoxNoResults from "../ComboBox/Components/ComboBoxNoResults";
import ComboBoxItem from "../ComboBox/Components/ComboBoxItem";
import {GetObjectIndexByKeyValue} from "../../../Constants/Helpers";
import useKeyboardNavigation from "../../../Hooks/useKeyboardNavigation";

export interface SelectFilterType {
    name: string;
    label: string;
    placeholder: string;

    data: Array<SelectItemType>;
    defaultValue?: any;

    error?: any;

    onChange: any;
    onBlur?: any;

    isLoading?: boolean;
    minimal?: boolean;
    listAlwaysVisible?: boolean;
}

const SelectFilter = (props: SelectFilterType) => {
    const [ displayValue, setDisplayValue ] = useState<string>('');
    const [ isListOpen, setIsListOpen ] = useState<boolean>(props.listAlwaysVisible ?? false);
    const [ selectedId, setSelectedId ] = useState<any>(null);
    const [ selectedItem, setSelectedItem ] = useState<any>({ label: '', value: null });

    const selectRef = useRef<HTMLDivElement>(null);
    const filterRef = useRef<HTMLInputElement>(null);

    const {
        filterSelectValue,
        filterSelectItems,
        FilterSelectList,
        ResetFilterSelectOptions,
        HandleFilterSelectBlur,
        HandleFilterSelectFocus
    } = useFilterSelectOptions({ filterData: props.data });

    useOutsiderDetector(selectRef, () => isListOpen ? UpdateDisplayValue() : null);

    const UpdateListVisibility = (state: boolean) => {
        setIsListOpen(props.listAlwaysVisible ?? state);
    };

    const HandleDefaultValue = (value: number) => {
        const itemIndex = GetObjectIndexByKeyValue(props.data, 'value', value);

        if(itemIndex > -1) {
            HandleSelection(null,  props.data[itemIndex]);
        } else {
            setSelectedItem({ label: '', value: null });
            setSelectedId(null);
        }
    };

    const HandleSelection = (e: any, item: any) => {
        const entry = item ? item : {
            label: e?.target.getAttribute('title'),
            value: parseInt(e?.target.getAttribute('data-value'))
        };

        if(e?.code && (e.code === 'ArrowDown' || e.code === 'ArrowUp')) {
            HandleKeyboardElementNavigation(e);
        } else {
            setSelectedItem(entry);
            setSelectedId(entry?.value);
            if(entry?.value !== props.defaultValue[0]) {
                props.onChange(entry?.value);
            }
        }
    };

    const UpdateDisplayValue = () => {
        const existsInData = GetObjectIndexByKeyValue(props.data, 'value', selectedItem?.value) > -1;

        setDisplayValue(props.isLoading ? 'Loading...' : selectedItem && existsInData ? selectedItem.label : '');
        setIsListOpen(false);
    };

    const UpdateFilterValue = () => {
        if(filterRef.current) {
            filterRef.current.value = isListOpen ? filterSelectValue : displayValue;
        }
    };

    const HandleFilterSelectBlurCallback = (e: any) => {
        if(isListOpen) {
            UpdateListVisibility(false);
        }

        if(props.onBlur) {
            props.onBlur(e);
        }
    };

    const HandleFilterSelectFocusCallback = () => {
        UpdateListVisibility(true);
    };

    const {
        HandleElementTransitionNavigation,
        HandleKeyboardElementNavigation,
        optionsCycled
    } = useKeyboardNavigation({
        itemClassName: 'item-menu-entry',
        itemContainerClassSelector: '.multi-select-active',
        resetCallback: ResetFilterSelectOptions,
        selectedCallback: HandleSelection
    });

    useEffect(() => {
        UpdateDisplayValue();
    }, [props.data, selectedItem]);

    useEffect(() => {
        UpdateFilterValue();
    }, [displayValue, isListOpen]);

    useEffect(() => {
        if(optionsCycled) {
            UpdateDisplayValue();
        }
        UpdateListVisibility(false);
    }, [optionsCycled]);

    useEffect(() => {
        if(selectedItem.value !== props.defaultValue) {
            HandleDefaultValue(props.defaultValue[0])
        }
    }, [props.defaultValue]);

    return (
        <div className={'flex-column multi-select-container flex-gap-lg'} ref={selectRef}>
            <div className={props.listAlwaysVisible && !props.minimal ? 'multi-select-inline-control' : ''}>
                <ComboBoxFilter
                    filterRef={filterRef}
                    filterSelectValue={filterSelectValue}
                    placeholder={props.placeholder}
                    label={props.label ?? 'ComboBox'}
                    resetCallback={(e: any) => ResetFilterSelectOptions()}
                    blurCallback={(e: any) => HandleFilterSelectBlur(e, HandleFilterSelectBlurCallback)}
                    filterActionCallback={(e: any) => FilterSelectList(e)}
                    filterNavigationCallback={(e: any) => HandleElementTransitionNavigation(e)}
                    filterFocusCallback={(e: any) => HandleFilterSelectFocus(e, HandleFilterSelectFocusCallback)}
                    minimal={props.minimal}
                    errorMessage={props.error?.message}
                    isValid={props.data && props.data.length && !props.error ? true : null}
                    isActive={isListOpen}
                    isLoading={props.isLoading}
                />

                <div className={`${props.listAlwaysVisible && !props.minimal ? 'multi-select-inline-list' : 'generic-menu-container'} ${props.minimal ? 'in-dropdown-menu' : ''} multi-select-entries ${isListOpen ? 'multi-select-active' : ''}`} style={{ maxHeight: '300px', overflow: 'auto'}} tabIndex={-1}>
                    {filterSelectItems && filterSelectItems.length === 0 && (
                        <ComboBoxNoResults />
                    )}

                    {filterSelectItems && filterSelectItems.length > 0 &&  filterSelectItems.map((item, index: number) => {
                        if(item.value === undefined || item.value === '') return null;

                        return (
                            <ComboBoxItem
                                key={`comboItem${props.label}${item.value}${index}`}
                                name={props.name}
                                label={props.label}
                                item={item}
                                selectedValues={selectedId}
                                onChangeCallback={(e: any, item: any) => HandleSelection(e, item)}
                                onKeyDownCallback={(e: any) => HandleKeyboardElementNavigation(e)}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
};

export default SelectFilter